import { updateMe, updateUser } from '@directus/sdk';
import { useMutation } from '@tanstack/react-query';

import { ADMIN_SELECTED_USER_ID_KEY } from '#constants/localstorage-keys.ts';

import { directusClient } from '../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../services/tanstackQueryClient.ts';
import { readMeWithAdminAccess } from './readMeWithAdminAccess.ts';

/**
 * Resolves who this update is actually for.
 *
 * readCurrentUser returns the impersonated promoter when an admin has picked one through
 * "Switch promoter". updateMe always writes to the logged-in account, so pairing the two meant
 * an admin editing a promoter's details silently overwrote their own name instead.
 */
const updateCurrentUser = async (updateData: { first_name: string; last_name: string }) => {
  const me = await readMeWithAdminAccess();
  const role = me.role as { admin_access?: boolean } | null;
  const selectedUserId = role?.admin_access === true ? localStorage.getItem(ADMIN_SELECTED_USER_ID_KEY) : null;

  if (selectedUserId && selectedUserId !== me.id) {
    await directusClient.request(updateUser(selectedUserId, updateData));
    return;
  }

  await directusClient.request(updateMe(updateData));
};

export const useUserUpdateMutation = () => {
  return useMutation({
    mutationFn: updateCurrentUser,
    onSuccess: async () => {
      await tanstackQueryClient.invalidateQueries({
        queryKey: ['user'],
      });
    },
  });
};
